import { Box, Tab, Tabs, Toolbar, Typography } from '@mui/material';
import Alert from 'components/alerts/Alert';
import React, { useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';

import { bindActionCreators } from 'redux';
import getSocket from 'services/getSocket';
import BatchSlice from 'store/app/entities/BatchSlice';
import alertActions from 'store/app/ui/Alert/action';
import isSuperAdmin from 'store/selectors/isSuperAdmin';
import { I18n } from 'utils/i18n';
import FailedJobsView from './FailedJobsView';
import JobExecutionHistory from './JobExecutionHistory';
import OverviewDashboard from './OverviewDashboard';
import RunningJobsView from './RunningJobsView';

const AdminBatchScene = () => {
  const socket = useRef(null);
  const dispatch = useDispatch();
  const isAdmin = useSelector(isSuperAdmin);
  const [tab, setTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  // Subscribe to batch events at parent level so subscription stays active across tab switches
  useEffect(() => {
    if (!isAdmin) {
      // Don't attempt subscription if user is not super admin
      return () => {};
    }

    const handleJobStarted = data => {
      // Update the cache directly with the new job information if cache exists
      dispatch(
        BatchSlice.util.updateQueryData('getRunningJobs', undefined, draft => {
          if (!draft) return;

          // Update queueStats if provided
          if (data?.queueStats) {
            // If running array is provided, use it as authoritative source
            if (data.queueStats.running) {
              draft.running = data.queueStats.running;
            }
            // Update queue statistics
            draft.queueStats = {
              ...draft.queueStats,
              totalQueued: data.queueStats.totalQueued ?? draft.queueStats?.totalQueued ?? 0,
              totalWorking: data.queueStats.totalWorking ?? draft.queueStats?.totalWorking ?? 0,
              totalRunning: data.queueStats.totalRunning ?? draft.queueStats?.totalRunning ?? 0,
              queued: data.queueStats.queued || draft.queueStats?.queued || {},
              workers: data.queueStats.workers || draft.queueStats?.workers || {},
            };
          }

          // If running array wasn't provided but we have job info, add it manually
          const hasRunningArray =
            Array.isArray(data?.queueStats?.running) && data.queueStats.running.length > 0;
          if (!hasRunningArray && data?.executionId) {
            const newJob = {
              executionId: data.executionId,
              jobName: data.jobName,
              batchConfigId: data.batchConfigId,
              startedAt: data.startedAt,
              workerId: data.workerId,
            };

            // Initialize structure if needed
            if (!draft.running) {
              draft.running = [];
            }

            // Check if job already exists in the list (avoid duplicates)
            const existingIndex = draft.running.findIndex(
              job => job.executionId === data.executionId,
            );
            if (existingIndex >= 0) {
              // Update existing job
              draft.running[existingIndex] = { ...draft.running[existingIndex], ...newJob };
            } else {
              // Add new job to the beginning of the list
              draft.running.unshift(newJob);
            }
          }
        }),
      );

      // Invalidate related tags (but NOT RunningJobs - socket is authoritative for that)
      dispatch(BatchSlice.util.invalidateTags([{ type: 'BatchJobs', id: 'LIST' }]));
    };

    const handleJobCompleted = data => {
      // Update the cache to remove completed job
      dispatch(
        BatchSlice.util.updateQueryData('getRunningJobs', undefined, draft => {
          if (!draft) return;

          // Update queueStats if provided
          if (data?.queueStats) {
            // If running array is provided, use it as authoritative source
            if (data.queueStats.running) {
              draft.running = data.queueStats.running;
            }
            // Update queue statistics
            draft.queueStats = {
              ...draft.queueStats,
              totalQueued: data.queueStats.totalQueued ?? draft.queueStats?.totalQueued ?? 0,
              totalWorking: data.queueStats.totalWorking ?? draft.queueStats?.totalWorking ?? 0,
              totalRunning: data.queueStats.totalRunning ?? draft.queueStats?.totalRunning ?? 0,
              queued: data.queueStats.queued || draft.queueStats?.queued || {},
              workers: data.queueStats.workers || draft.queueStats?.workers || {},
            };
          }

          // If running array wasn't provided but we have job info, remove it manually
          if (!data?.queueStats?.running && data?.executionId && draft.running) {
            draft.running = draft.running.filter(job => job.executionId !== data.executionId);
          }
        }),
      );

      // Invalidate tags to trigger refetches for other queries
      dispatch(
        BatchSlice.util.invalidateTags([
          { type: 'BatchJobs', id: 'LIST' },
          { type: 'JobExecution', id: 'LIST' },
          { type: 'FailedJobs', id: 'LIST' },
        ]),
      );
    };

    socket.current = getSocket('adminBatch');
    socket.current.connect();
    socket.current.on('batch:job:started', handleJobStarted);
    socket.current.on('batch:job:completed', handleJobCompleted);

    return () => {
      socket.current.disconnect();
    };
  }, [isAdmin, dispatch]);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Toolbar>
        <Typography variant="h3" sx={{ flexGrow: 1 }}>
          {I18n.t('SuperAdminMenu.batch') || 'Batch Jobs Monitoring'}
        </Typography>
      </Toolbar>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab label={I18n.t('AdminBatchScene.Overview') || 'Overview'} />
          <Tab label={I18n.t('AdminBatchScene.Running') || 'Running Jobs'} />
          <Tab label={I18n.t('AdminBatchScene.History') || 'Execution History'} />
          <Tab label={I18n.t('AdminBatchScene.Failures') || 'Failed Jobs'} />
        </Tabs>
      </Box>
      <Box sx={{ padding: 2, paddingBottom: 5, flex: 1, overflow: 'auto' }}>
        {tab === 0 && <OverviewDashboard />}
        {tab === 1 && <RunningJobsView />}
        {tab === 2 && <JobExecutionHistory />}
        {tab === 3 && <FailedJobsView />}
      </Box>
      <Alert isOpen={false} variant="info" label="" caption="" />
    </Box>
  );
};

const mapDispatchToProps = dispatch => ({
  actions: {
    alert: bindActionCreators(alertActions, dispatch),
  },
});

export default connect(null, mapDispatchToProps)(AdminBatchScene);
